import { CREDIT_FEATURES, } from "../redux/permissions/featureCatalog";
/**
 * Phrasing for the daily allowance.
 *
 * The grant size (`daily.limit`) is per-user — the A/B test currently splits 1
 * vs 5, but it is just a number from the backend — so no screen may hardcode
 * one, and every sentence has to read correctly at 1 (singular), at any N
 * (plural), and with no number at all.
 *
 * That last case is not an edge case. A logged-out visitor on /subscription
 * never calls `GET /credits`, and no grant is assigned until they have an
 * account, so "free AI credits every day" is the *true* sentence for them — not
 * a degraded one. The same phrasing covers an unlimited tier, and any deploy
 * window where `limit` isn't being sent yet.
 */
/** "credit" or "credits", agreeing with `count` (plural when unknown). */
export const creditNoun = (count) => count === 1 ? "credit" : "credits";
/**
 * "5 free AI credits" | "1 free AI credit" | "free AI credits" with no grant.
 *
 * Both branches name the same thing, and have to keep naming it the same way:
 * whether a limit is known is a loading/auth detail, not a product distinction,
 * and both call sites can render either variant on the same screen. When the
 * "AI" wording landed it was applied only to the number-free branch, so a
 * logged-out visitor read "free AI credits" where a logged-in one read "5 free
 * credits" — the same sentence, two different nouns.
 *
 * A number is only worth stating when there is one to state: a limit of 0 (or a
 * NaN off the wire) takes the number-free branch, because "0 free AI credits
 * every day" advertises the absence of the thing the bullet is selling.
 *
 * That leans on the backend never sending a real 0 — true today, where the only
 * grants are the A/B test's 1 and 5, and where the guard exists mainly to keep a
 * NaN from rendering as "NaN free AI credits". If a genuine zero grant ever does
 * ship, this branch stops being a formatting choice and starts being a false
 * claim: `undefined` means "your grant isn't known yet", for which "free AI
 * credits every day" is honest, but 0 means "you get none", for which it is not.
 * The fix then is to drop the bullet on FreePlan rather than reword it — there
 * is no honest phrasing for a daily allowance of nothing.
 *
 * `sentenceStart` capitalises only the number-free variant, and only because
 * that variant is the one whose first word is a word. With a grant the phrase
 * opens on a digit, so "5 Free AI credits" would be wrong wherever it appeared —
 * the case has to follow the shape of the phrase, not the call site alone.
 */
export const dailyLimitNoun = (limit, opts) => {
    if (typeof limit === "number" && limit > 0)
        return `${limit} free AI ${creditNoun(limit)}`;
    return `${opts?.sentenceStart ? "Free AI" : "free AI"} ${creditNoun()}`;
};
/**
 * What credits do *not* apply to — the reassurance both credit dialogs end on.
 *
 * One list, because the two surfaces are the ones most likely to be seen back
 * to back, and they had already drifted: one named evolutions among the free
 * things and the other didn't, so the app made two different promises about
 * whether browsing evolutions costs anything. It does not.
 */
export const EVERYTHING_ELSE_FREE_LEAD = "Everything else stays free:";
export const EVERYTHING_ELSE_FREE_EXAMPLES = "SBC solutions from the market, player ratings, evolutions and browsing your club.";
/** Verb agreement for a sentence whose subject is `dailyLimitNoun`. */
export const dailyLimitVerb = (limit, singular, plural) => (limit === 1 ? singular : plural);
/**
 * "3 of 5" with a known grant, "3" without.
 *
 * The denominator is what makes a low balance legible: "1 left" reads as broken,
 * "1 of 5 left" reads as a budget.
 */
export const balanceCount = (remaining, limit) => typeof limit === "number" ? `${remaining} of ${limit}` : `${remaining}`;
/** The noun that follows `balanceCount`, agreeing with its last number. */
export const balanceNoun = (remaining, limit) => creditNoun(typeof limit === "number" ? limit : remaining);
/**
 * The sign-up bonus, framed as a gift rather than a balance.
 *
 * "Welcome credits" rather than "the free credits we gave you when you signed
 * up": it names them as their own thing, so they can't be confused with the
 * daily grant sitting in the sentence above, and it carries "at the start"
 * without spending a clause on it.
 *
 * Two sentences, because the empty case is not just "0 of 20": the point is
 * that the gift was real and is now spent, which is what makes the daily
 * allowance the thing to pay attention to from here on.
 */
export const signupBonusSentence = (remaining, grant) => remaining > 0
    ? `You have ${remaining} of your ${grant} free welcome ${creditNoun(grant)} left.`
    : `You've used all ${grant} of your free welcome ${creditNoun(grant)}.`;
/**
 * Names what a refusal actually blocked, for the out-of-credits dialog.
 *
 * "Out of credits" on its own reads as "the product stopped working". The point
 * of the sentence is that one specific club-powered action has a price and the
 * rest of EasySBC does not, so the user can carry on instead of bouncing.
 */
export const blockedLine = (featureId, cost) => {
    // A cost of 0 means the backend never quoted this feature, so any number here
    // would be invented — fall back to the generic sentence.
    if (!featureId || cost <= 0) {
        return "Actions that use the players in your own club cost credits.";
    }
    return `${CREDIT_FEATURES[featureId].label} costs ${cost} ${creditNoun(cost)}.`;
};
/**
 * The same dialog opened as the last credit went, not on a refusal: the action
 * just happened, so "costs" would read as a block that never came. Leads with
 * the fact, then names the price the way `blockedLine` does.
 */
export const spentLine = (featureId, cost) => {
    if (!featureId || cost <= 0) {
        return "That was your last credit for now. Actions that use the players in your own club cost credits.";
    }
    return `That was your last credit for now: ${CREDIT_FEATURES[featureId].label} costs ${cost} ${creditNoun(cost)}.`;
};
/** The dialog's lead line for either way in. */
export const outOfCreditsLine = (reason, featureId, cost) => reason === "spent"
    ? spentLine(featureId, cost)
    : blockedLine(featureId, cost);
//# sourceMappingURL=creditsCopy.js.map